// kitchen/web/src/views/TableBoard.tsx
// Per-table KDS board view.
// Consumes useOrders + useClientConfig, groups by table, renders TableCard
// in a horizontal scrolling row with ◀ ▶ navigation buttons (US4).

import { useMemo, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useOrders } from "../hooks/useOrders";
import { useClientConfig } from "../hooks/useClientConfig";
import { useKitchenStore } from "../stores/kitchen.store";
import {
  groupOrdersByTable,
  sortTables,
  oldestActiveItemId,
  isActiveKitchenItem,
  filterOrdersByStation,
  stationBoardConfig,
} from "@shared/kds/table-board";
import TableCard from "../components/TableCard";
import { useDragScroll } from "../hooks/useDragScroll";
import { useTranslation } from "../i18n";

// ─── Scroll step: one card width (320px) or a sensible fixed amount ───────────
/**
 * One press of ◀ ▶ moves one column of cards. The card width follows the chosen type size,
 * so it is read from the live variable rather than frozen in a constant — a
 * fixed number would move two cards at the small size and half a card at the
 * large one.
 */
function scrollStep(): number {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--kds-card-min");
    const parsed = parseInt(raw.trim(), 10);
    // Plus the gap between two columns.
    if (Number.isFinite(parsed) && parsed > 0) return parsed + 16;
  } catch {
    // Fall through to the default below.
  }
  return 360;
}

interface TableBoardProps {
  clientId: string;
}

export default function TableBoard({ clientId }: TableBoardProps) {
  const { t } = useTranslation();
  const { data: orders, isLoading: ordersLoading } = useOrders(clientId);
  const { config: clientConfig } = useClientConfig(clientId);

  // Single-station monitor (`?station=<id>`, e.g. the bar): only that
  // station's items, with a board config that keeps their categories visible
  // regardless of the client's kitchen mapping.
  const stationFilter = useKitchenStore((s) => s.stationFilter);
  const stations = useKitchenStore((s) => s.stations);
  const scopedOrders = useMemo(
    () =>
      stationFilter ? filterOrdersByStation(orders ?? [], stationFilter) : (orders ?? []),
    [orders, stationFilter],
  );
  const config = useMemo(() => {
    if (!stationFilter) return clientConfig;
    const name =
      stations.find((s) => s.id === stationFilter)?.name ?? t("kds.station.fallbackName", "Stazione");
    return stationBoardConfig(clientConfig, scopedOrders, name);
  }, [clientConfig, stationFilter, stations, scopedOrders, t]);

  // US3 + timer mm:ss: ticka ogni secondo così i timer per-piatto scorrono
  // in tempo reale (e i colori ritardo si aggiornano di conseguenza).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  // US4: ref to the scrollable container for programmatic scroll + visibility.
  // Measured, moved by the two arrows, and dragged sideways. The till reports a
  // finger as a mouse, so a plain swipe moves nothing (`lib/drag-scroll.ts`);
  // the queue inside each table card already got this from `ScrollPane`.
  const scrollRef = useDragScroll<HTMLDivElement>("x");
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Recompute scroll button visibility.
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Se c'è overflow orizzontale mostra SEMPRE entrambe le frecce, così lo
    // the cook can always get back after scrolling right.
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    setCanScrollLeft(hasOverflow);
    setCanScrollRight(hasOverflow);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });

    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  // Also recompute after data changes (number of cards may change).
  const tableGroupsLength = useMemo(() => {
    return (orders ?? []).length;
  }, [orders]);

  useEffect(() => {
    // Allow one frame for the DOM to update before measuring.
    const raf = requestAnimationFrame(updateScrollState);
    return () => cancelAnimationFrame(raf);
  }, [tableGroupsLength, updateScrollState]);

  // Filter out COMPLETED and CANCELLED orders (same as ChefOverview station view).
  const activeOrders = useMemo(
    () =>
      scopedOrders.filter(
        (o: any) => o.status !== "COMPLETED" && o.status !== "CANCELLED",
      ),
    [scopedOrders],
  );

  // Group into table groups.
  const tableGroups = useMemo(
    () => groupOrdersByTable(activeOrders, config, t),
    [activeOrders, config, t],
  );

  // FR-011: scarta i gruppi senza piatti CUCINA attivi (categoria mappata).
  // A table holding only unmapped drinks does not belong on the cook's board.
  const activeGroups = useMemo(
    () => tableGroups.filter((g) => g.items.some((i) => isActiveKitchenItem(i, config))),
    [tableGroups, config],
  );

  // US3 (FR-013): sort by wait descending, respecting takeawayPosition config.
  const sortedGroups = useMemo(
    () => sortTables(activeGroups, config, now),
    [activeGroups, config, now],
  );

  // FR-015: single oldest active item id across all visible groups.
  const oldestId = useMemo(
    () => oldestActiveItemId(sortedGroups, now, config),
    [sortedGroups, now, config],
  );

  if (ordersLoading) {
    return (
      <div className="flex items-center justify-center flex-1 text-kds-muted">
        <span className="w-6 h-6 border-2 border-kds-muted border-t-kds-text rounded-full animate-spin mr-3" />
        {t("kds.board.loadingOrders", "Caricamento ordini…")}
      </div>
    );
  }

  if (sortedGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 text-kds-muted gap-2">
        <p className="text-kds-title">{t("kds.board.noTables", "Nessun tavolo in servizio")}</p>
        <p className="text-kds-note">{t("kds.board.tablesAppearHint", "I tavoli aperti appariranno qui automaticamente")}</p>
      </div>
    );
  }

  // The arrows are always mounted: live when there is somewhere to go, dead
  // otherwise. They never appear and disappear under the cook's hand, which
  // sparire del tutto → niente confusione/blocchi.
  const activeBtn =
    "bg-red-600 text-white border-2 border-red-300 shadow-xl animate-pulse active:scale-95";
  const disabledBtn =
    "bg-kds-card/60 text-kds-muted border-2 border-kds-muted/20 cursor-default";

  return (
    // Outer wrapper: relative for absolute-positioned scroll buttons (US4).
    // min-h-0 is load-bearing: without it the flex item grows with its content
    // di default) e le card escono dallo schermo invece di scrollare.
    <div className="flex-1 min-h-0 relative overflow-hidden flex">
      {/* US4 ◀ left scroll button — sempre visibile */}
      <button
        type="button"
        aria-label={t("kds.board.scrollLeft", "Scorri a sinistra")}
        disabled={!canScrollLeft}
        onClick={() => {
          scrollRef.current?.scrollBy({ left: -scrollStep(), behavior: "smooth" });
        }}
        className={[
          "absolute left-0 top-1/2 -translate-y-1/2 z-20",
          "flex items-center justify-center",
          "w-12 h-24 rounded-r-xl",
          canScrollLeft ? activeBtn : disabledBtn,
        ].join(" ")}
      >
        <ChevronLeft size={36} strokeWidth={3} />
      </button>

      {/* Horizontal scrolling row of TableCards (FR-017/FR-019). */}
      <div
        ref={scrollRef}
        className="flex-1 min-w-0 h-full overflow-x-auto overflow-y-hidden"
        style={{ scrollbarWidth: "thin" }}
      >
        {/* Cards are as tall as their dishes, and stack top to bottom before
            starting the next column — a CSS multi-column box with a fixed
            height, whose extra columns overflow to the right and scroll.
            Each card used to be as tall as the screen, so a table with one
            dish left most of a column empty and pushed the next table off
            the screen (Sun Club, 23/09/2026). */}
        <div
          className="h-full px-1 py-1"
          style={{
            columnWidth: "var(--kds-card-min)",
            columnGap: "1rem",
            columnFill: "auto",
          }}
        >
          {sortedGroups.map((group) => (
            // A card never splits across two columns, and never grows past the
            // screen: a longer one scrolls inside itself, as before.
            <div
              key={group.key}
              className="flex flex-col max-h-full mb-3"
              style={{ breakInside: "avoid" }}
            >
              <TableCard
                group={group}
                liveGroups={sortedGroups}
                config={config}
                oldestItemId={oldestId}
                now={now}
                flat={!!stationFilter}
              />
            </div>
          ))}
        </div>
      </div>

      {/* US4 ▶ right scroll button — sempre visibile */}
      <button
        type="button"
        aria-label={t("kds.board.scrollRight", "Scorri a destra")}
        disabled={!canScrollRight}
        onClick={() => {
          scrollRef.current?.scrollBy({ left: scrollStep(), behavior: "smooth" });
        }}
        className={[
          "absolute right-0 top-1/2 -translate-y-1/2 z-20",
          "flex items-center justify-center",
          "w-12 h-24 rounded-l-xl",
          canScrollRight ? activeBtn : disabledBtn,
        ].join(" ")}
      >
        <ChevronRight size={36} strokeWidth={3} />
      </button>
    </div>
  );
}
